import {
    AutocompleteOption,
    AutocompleteOptionsProps,
    getSelectedValue,
} from "components/Form/components/Autocomplete/Autocomplete";
import {
    autocompleteOptionsListCountSelector,
    searchCountSelector,
    searchResultsSelector,
    stateOptionsSelector,
} from "components/Form/components/Autocomplete/Autocomplete.selectors";
import { fetchAutocomplete } from "components/Form/components/Autocomplete/Autocomplete.service";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSearchObj, isUuid, SingleField } from "utils/utils";
import "./Autocomplete.scss";

export const useAsyncSelect = (
    options: AutocompleteOptionsProps,
    value: any,
    iri: string,
    defaultValue?: AutocompleteOption,
    hasInitialValuesLoaded?: boolean,
    initialAutocompleteOption?: AutocompleteOption | null
) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoadedOptions, setHasLoadedOptions] = useState(false);
    const [term, setTerm] = useState("");
    const currentPageRef = useRef(0);
    const hasFetchedInitialOption = useRef(false);
    // The last value for which we issued a single-id label lookup. Tracking the
    // resolved value (rather than a boolean latch) lets the label re-resolve
    // whenever the bound value changes to a not-yet-resolved UUID.
    const lastResolvedValueRef = useRef<any>(undefined);

    const [selectedOptionCache, setSelectedOptionCache] = useState<AutocompleteOption | null>(null);

    const dynamicOptions = useSelector(stateOptionsSelector(iri), isEqual);
    const dynamicOptionsCount = useSelector(autocompleteOptionsListCountSelector(iri));

    const searchResults = useSelector(searchResultsSelector(iri));
    const searchCount = useSelector(searchCountSelector(iri));

    const hasMore = useMemo(() => {
        if (!term) return dynamicOptionsCount > (dynamicOptions?.length || 0);
        return searchCount > (searchResults?.length || 0);
    }, [term, searchCount, searchResults?.length, dynamicOptions?.length, dynamicOptionsCount]);

    // Merge dynamic options with cached selected option
    const mergedOptions = useMemo(() => {
        if (!dynamicOptions) return [];

        if (
            selectedOptionCache &&
            !dynamicOptions.find((opt) => opt.value === selectedOptionCache.value)
        ) {
            return [selectedOptionCache, ...dynamicOptions];
        }

        return dynamicOptions;
    }, [dynamicOptions, selectedOptionCache]);

    const displayOptions = useMemo(() => {
        if (term) {
            if (
                selectedOptionCache &&
                !searchResults?.find((opt) => opt.value === selectedOptionCache.value)
            ) {
                return [selectedOptionCache, ...(searchResults || [])];
            }
            return searchResults || [];
        }
        return mergedOptions;
    }, [term, searchResults, mergedOptions, selectedOptionCache]);

    const fetchOptions = useCallback(
        async (inputValue, singleOptionQueryField?: SingleField) => {
            if (!options.resource) return;
            setIsLoading(true);

            const limit = singleOptionQueryField ? 1 : 20;
            const offset = currentPageRef.current * limit;

            const relevantCount = inputValue ? searchCount : dynamicOptionsCount;
            if (offset !== 0 && relevantCount < offset) {
                setIsLoading(false);
                return;
            }

            // Resolve the label of the already-selected value even when the
            // referenced record is in trash: the single-id resolution query
            // (limit === 1) always includes allow_deleted. The pick-list search
            // (limit === 20) is deliberately left trash-excluding so picking a
            // NEW reference still does not surface deleted records.
            const additionalQueryParams =
                limit === 1
                    ? {
                          allow_deleted: true,
                      }
                    : {};

            return new Promise((resolve, reject) => {
                fetchAutocomplete({
                    dispatch,
                    promise: { resolve, reject },
                    options,
                    inputValue,
                    defaultValue,
                    params: { limit, offset },
                    singleOptionQueryField,
                    iri,
                    search: !!inputValue,
                    additionalQueryParams,
                });
            })
                .then((response: any) => {
                    const newOptions = options.filterOptions
                        ? response.filter(options.filterOptions)
                        : response;

                    if (singleOptionQueryField && newOptions?.[0]) {
                        setSelectedOptionCache(newOptions[0]);
                    }

                    if (inputValue) {
                        dispatch({
                            type: "STORE_SEARCH_RESULTS",
                            iri,
                            response: newOptions,
                            overwritePrevValue: offset === 0, // Reset results on new search
                        });
                    }
                    return newOptions;
                })
                .catch((error) => {
                    console.warn("fetchAutocomplete failed", error);
                    return [];
                })
                .finally(() => setIsLoading(false));
        },
        [options, searchCount, dynamicOptionsCount, dispatch, defaultValue, iri]
    );

    const loadOptions = useCallback(
        async (inputValue?: string) => {
            setTerm(inputValue || "");

            if (!hasLoadedOptions) {
                currentPageRef.current = 0;
            } else {
                currentPageRef.current += 1;
            }
            const result = await fetchOptions(inputValue);
            setHasLoadedOptions(true);
            return result;
        },
        [hasLoadedOptions, fetchOptions]
    );

    const handleMenuOpen = useCallback(async () => {
        if (hasLoadedOptions) {
            currentPageRef.current = -1;
        }
        await loadOptions("");
    }, [hasLoadedOptions, loadOptions]);

    // Effect to resolve the label for the currently-selected value.
    useEffect(() => {
        // Prefer an explicitly-provided initial option on first resolution.
        if (
            initialAutocompleteOption &&
            !hasFetchedInitialOption.current &&
            (hasInitialValuesLoaded === undefined || hasInitialValuesLoaded === true)
        ) {
            setSelectedOptionCache(initialAutocompleteOption);
            hasFetchedInitialOption.current = true;
            lastResolvedValueRef.current = initialAutocompleteOption.value;
            return;
        }

        const resolved = getSelectedValue({
            value,
            defaultValue,
            options: dynamicOptions,
        });

        // The label for `value` is already known if the loaded options resolve
        // it (getSelectedValue found a real label, i.e. not its raw-uuid
        // fallback) or the selected-option cache already holds it. The cache
        // matters because the pick-list options are replaced as the user
        // paginates/reopens, so a previously-resolved selection can drop out of
        // `dynamicOptions` while still being correctly cached — we must not
        // re-query in that case.
        const labelAlreadyKnown =
            (!!resolved && resolved.label !== value) ||
            (selectedOptionCache?.value === value && selectedOptionCache?.label !== value);

        // Otherwise, when `value` is a UUID whose label we cannot resolve and we
        // have not already issued a lookup for this exact value, resolve it with
        // a single-id query. This re-fires whenever the bound value changes to
        // an unresolved UUID (e.g. after a record reload where the reference
        // changed), not only on first mount — the bug this replaces (the old
        // boolean latch never reset, so a changed value kept showing the raw
        // UUID).
        const needsSingleIdLookup =
            (hasInitialValuesLoaded === undefined || hasInitialValuesLoaded === true) &&
            value &&
            isUuid(value) &&
            !labelAlreadyKnown &&
            lastResolvedValueRef.current !== value;

        if (needsSingleIdLookup) {
            lastResolvedValueRef.current = value;
            hasFetchedInitialOption.current = true;
            const singleQueryField = getSearchObj({
                field: "id",
                op: "eq",
                value,
            });
            fetchOptions("", singleQueryField);
            return;
        }

        // Otherwise keep the cache in sync with what the loaded options resolve
        // to. The value-guard preserves an already-resolved label (same value)
        // so a later dynamicOptions change that still lacks the record cannot
        // clobber the label back to the raw UUID.
        setSelectedOptionCache((prevValue) => {
            if (prevValue?.value !== resolved?.value) {
                return resolved;
            }
            return prevValue;
        });
    }, [hasInitialValuesLoaded, fetchOptions, value, defaultValue, dynamicOptions, initialAutocompleteOption, hasLoadedOptions, selectedOptionCache]);

    useEffect(() => {
        if (!term) {
            dispatch({ type: "CLEAR_SEARCH_RESULTS", iri });
        }
    }, [term, dispatch, iri]);

    return {
        isLoading,
        term,
        setTerm,
        hasMore,
        loadOptions,
        handleMenuOpen,
        currentPageRef,
        dynamicOptions: displayOptions,
        hasLoadedOptions,
        dynamicSelectedOption: selectedOptionCache,
    };
};
